/** My Site (Demo Mode only) - site-facing summary for a single site's team, under Site
 * Operations. Simplified version of the central Estate Overview scoped to "my" site only. No
 * backend site-scoped dashboard exists yet - all data is static mock data, matching the same
 * "seeded illustrative numbers" approach used by src/pages/EstateManagement. */

import { useState } from 'react';
import { Box, Grid, SvgIcon, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { CheckCircle, Groups, Restaurant, Build } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import { TableHeader } from '../../components/forms/TableHeader';
import { Pill } from '../../components/shared/Pill';
import CustomPagination from '../../components/forms/Pagination';

const SITE = {
  name: 'St Mary\'s School',
  status: 'Live',
  customer: 'Thomas Franks',
  siteType: 'Education Catering',
};

const RECENT_REQUESTS = [
  { id: 'SR-1039', issue: 'Combi oven service', status: 'Resolved', raised: '2026-05-20' },
  { id: 'SR-1044', issue: 'Servery counter light out', status: 'In Progress', raised: '2026-06-03' },
];

const MySite = () => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const [page, setPage] = useState(0);

  const tiles = [
    {
      label: t('estateManagementStatus', 'Status'),
      value: SITE.status,
      subtitle: t('mySiteName', 'Site'),
      icon: <CheckCircle />,
      color: '#2e7d32',
    },
    {
      label: t('siteManagementCustomer', 'Customer'),
      value: SITE.customer,
      subtitle: SITE.name,
      icon: <Groups />,
      color: '#1976d2',
    },
    {
      label: t('siteDevelopmentSiteType', 'Site Type'),
      value: SITE.siteType,
      subtitle: SITE.name,
      icon: <Restaurant />,
      color: '#ed6c02',
    },
    {
      label: t('mySiteRecentRequests', 'Recent service requests'),
      value: `${RECENT_REQUESTS.length}`,
      subtitle: t('mySiteRequestsPillLabel', 'Requests'),
      icon: <Build />,
      color: '#9c27b0',
    },
  ];

  return (
    <PageArea>
      <PageTitle>
        {t('mySiteTitle', 'My Site')}
      </PageTitle>

      <Grid container
        spacing={4}>
        {tiles.map((tile) => (
          <Grid item
            key={tile.label}
            xs={12}
            sm={6}
            md={3}>
            <Section style={{ p: 4, mb: 0 }}>
              <Box display="flex"
                alignItems="center"
                justifyContent="space-between">
                <Box flex={1}>
                  <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                    {tile.label}
                  </Box>
                  <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                    {tile.value}
                  </Box>
                  <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
                    {tile.subtitle}
                  </Box>
                </Box>
                <Box sx={{ backgroundColor: `${tile.color}20`, borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tile.color }}>
                  <SvgIcon fontSize="medium">{tile.icon}</SvgIcon>
                </Box>
              </Box>
            </Section>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 6 }}>
        <Section>
          <TableHeader>
            {t('mySiteRecentRequests', 'Recent service requests')}
            <Pill text={`${RECENT_REQUESTS.length} ${t('mySiteRequestsPillLabel', 'Requests')}`} />
          </TableHeader>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('serviceRequestsId', 'Request ID')}</TableCell>
                  <TableCell>{t('siteEquipmentHistoryType', 'Issue')}</TableCell>
                  <TableCell>{t('estateManagementStatus', 'Status')}</TableCell>
                  <TableCell>{t('serviceRequestsRaised', 'Raised')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {RECENT_REQUESTS.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.id}</TableCell>
                    <TableCell>{r.issue}</TableCell>
                    <TableCell>{r.status}</TableCell>
                    <TableCell>{r.raised}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomPagination
            page={page}
            setPage={setPage}
            maxPages={1}
            breakpointForChangeDisplay={120000}
            useNoDataMessage={false}
          />
        </Section>
      </Box>
    </PageArea>
  );
};

export default MySite;
